import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Message } from '../models/message';
import { LoggingService } from './logging.service';
import { EventService } from './event.service';

@Injectable({
  providedIn: 'root'
})
export class MessageService {

  // eslint-disable-next-line no-underscore-dangle, id-blacklist, id-match
  private _messages: Message[] = [];

  constructor(
    private api: ApiService,
    private events: EventService,
    private logger: LoggingService,
  ) {
  }

  // ====================================== //
  // ===== Public convenience getters ===== //
  // ====================================== //

  public get hasMessages(): boolean {
    return this._messages && this._messages.length > 0;
  }

  public get hasNotification(): boolean {
    return this.notifications.length > 0;
  }

  public get infos(): Message[] {
    return (this._messages || []).filter(message => message.isInfo);
  }

  public get warnings(): Message[] {
    return (this._messages || []).filter(message => message.isWarning);
  }

  public get successes(): Message[] {
    return (this._messages || []).filter(message => message.isSuccess);
  }

  public get notifications(): Message[] {
    return (this._messages || []).filter(message => message.isNotification);
  }

  public get all(): Message[] {
    return this.infos.concat(this.successes).concat(this.notifications).concat(this.warnings);
  }

  // ======================================== //
  // ===== API related public functions ===== //
  // ======================================== //

  public getUnreadMessages(): Promise<boolean> {
    return this.api.getUnreadMessages()
      .then(messages => {
        this.removeMultiple(this.notifications, true);
        this.addMultiple(messages);
        return messages.length > 0;
      });
  }

  public markAsRead(message: Message): Promise<void> {
    return this.api.markMessageAsRead(message)
      .then(() => this.remove(message));
  }

  // ===================================== //
  // ===== Public message operations ===== //
  // ===================================== //

  /**
   * Clear all messages that are not required to be dismissed by the user
   */
  public clear(): void {
    this.removeMultiple(this.infos);
    this.removeMultiple(this.warnings);
    this.removeMultiple(this.successes);
  }

  /**
   * Add a new message
   *
   * @param message New message
   * @param [removeAfterMs] Optionally remove the message after n milliseconds
   */
  public add(message: Message, removeAfterMs?: number): void {
    if (!this.has(message)) {
      if (message.isSuccess || message.isWarning) {
        this.removeMultiple(this.warnings);
      }
      this._messages.push(message);
      this.logger.log(`📩 New message: ${message.text}`, message);
      this.notify();

      if (removeAfterMs !== undefined && removeAfterMs !== null) {
        setTimeout(() => this.remove(message), removeAfterMs);
      }
    }
  }

  /**
   * Add an array of messages
   *
   * @param messages The messages to add
   */
  public addMultiple(messages: Message[]): void {
    messages.reverse().forEach(message => this.add(message));
  }

  /**
   * Remove a message
   *
   * @param message The message to remove
   * @param silent If the change should not be propagated
   */
  public remove(message: Message, silent: boolean = false): void {
    const index = this.getIndexOf(message);
    if (index > -1) {
      this._messages.splice(index, 1);
      this.logger.log(`📭 Removed message: ${message.text}`, message);
    }

    if (!silent) {
      this.notify();
    }
  }

  /**
   * Remove multiple messages
   *
   * @param messages The messages to remove
   * @param silent If the change should not be propagated
   */
  public removeMultiple(messages: Message[], silent: boolean = false): void {
    messages.forEach(message => this.remove(message, silent));
  }

  // ============================= //
  // ===== Private functions ===== //
  // ============================= //

  /**
   * Returns if the passed message already exists in the messages or not
   *
   * @param message The message to check
   * @returns If the message already exists in the messages or not
   */
  private has(message: Message): boolean {
    return this.getIndexOf(message) > -1;
  }

  /**
   * The index of a message in the message array
   *
   * @param message2 The message to look for
   * @returns Index
   */
  private getIndexOf(message2: Message): number {
    return this._messages.findIndex(message1 => message1.id === message2.id);
  }

  /**
   * Send out an event that the messages have changed, so other parts can react accordingly
   */
  private notify(): void {
    this.logger.log('📬 Messages changed', this.all);
    this.events.messagesChanged.next();
  }
}
